import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {Inject, Injectable, Injector, Optional, PLATFORM_ID} from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { UserService } from '../services/user.service';
import { isPlatformBrowser } from '@angular/common';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { environment } from 'src/environments/environment';
import { SignOutService } from '../services/sign-out.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor
{
    /// The server's word that this sign-in is over (SignInCutoff): ended by the owner, by a password reset or a shop closed
    /// for good, or simply run out. Acted on once.
    private static readonly signedOutHeader = 'X-Signed-Out';
    private signingOut = false;

    constructor(private userService:UserService,@Inject(PLATFORM_ID) private platformId: any,
                @Optional() @Inject(REQUEST) private request: any,
                private injector: Injector)
    {

    }

    /// Signed out here too, once: the page reloads as a guest's, and signing in again makes a new sign-in. SignOutService is
    /// fetched only now - it needs the basket's services, which need HttpClient, which needs this interceptor.
    private endSession(headers: any, sentToken: string): void
    {
        if (!sentToken || this.signingOut || !isPlatformBrowser(this.platformId) || !headers || !headers.get(AuthInterceptor.signedOutHeader))
        {
            return;
        }
        this.signingOut = true;
        // Another tab signed in again since this one read its token: that sign-in is the one in use - this tab reloads with
        // it rather than wiping it.
        let stored: string = null;
        try { stored = window.localStorage.getItem('cul_culture_tm_stmp_temp'); } catch { }
        if (stored && stored !== sentToken)
        {
            window.location.reload();
            return;
        }
        try { this.injector.get(SignOutService).signOut(); }
        catch { this.signingOut = false; }
    }

    /// On the server only: the country of the visitor this page is rendered for - Cloudflare's
    /// placing of their request, else the "iso" cookie - so the lists in the first paint are the
    /// ones the browser gets when it asks again, not every product (the render has no country of
    /// its own) followed by fewer. The API believes this header from its own machine only.
    ///
    /// The signed-in owner is the exception: the render has no token, and the browser shows them
    /// every product (or, seeing the shop as another country, that country's), so for them it
    /// names the country they are seeing the shop as, or none. The cookies saying so are theirs
    /// (CountryGateService); anyone else who sets them only changes their own first paint. Asking
    /// for the currency (Locale/me, forLocale), the owner not seeing the shop as another country
    /// is named where they are, as their browser is.
    private visitorCountry(forLocale: boolean): string
    {
        if (isPlatformBrowser(this.platformId) || !this.request || !this.request.headers) { return null; }

        const valid = (c: string) => /^[A-Z]{2}$/.test(c) && c !== 'XX' && c !== 'T1';
        const cookie = (name: string) => this.requestCookie(name);

        if (cookie('izz_staff') === '1')
        {
            const viewAs = cookie('izz_view_as');
            if (valid(viewAs)) { return viewAs; }
            if (!forLocale) { return null; }
        }

        const placed = String(this.request.headers['cf-ipcountry'] || '').trim().toUpperCase();
        if (valid(placed)) { return placed; }

        const iso = cookie('iso');
        return valid(iso) ? iso : null;
    }

    /// On the server only, asking for the currency (Locale/me) for the owner's page: the currency they picked in the top
    /// bar (izz_owner_cur), which the API believes from its own machine only (OwnerCurrency) - so their page is drawn in
    /// it from the first paint. The same cookies as the country above; anyone else who sets them only changes their own.
    private ownerCurrency(): string
    {
        if (isPlatformBrowser(this.platformId) || !this.request || !this.request.headers) { return null; }
        if (this.requestCookie('izz_staff') !== '1') { return null; }
        const code = this.requestCookie('izz_owner_cur');
        return /^[A-Z]{3}$/.test(code) ? code : null;
    }

    /// One cookie of the page request this render is for, upper case, or ''.
    private requestCookie(name: string): string
    {
        const m = new RegExp('(?:^|;\\s*)' + name + '=([^;]*)').exec(String(this.request.headers['cookie'] || ''));
        try { return m ? decodeURIComponent(m[1]).trim().toUpperCase() : ''; }
        catch { return ''; }
    }
  
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> 
    {
        var token = this.userService.utoken;  
        const ipurl = "https://api.ipstack.com/check?access_key=4c87fc734befdeb582c3c1227a33d0cc"; 

        if (req.url === ipurl)
        {
            return next.handle(req);
        }  
        else
        {

            const headers: { [name: string]: string } = {
                language: 'en',
                ContentType: 'application/json; charset=utf-8'
            };
            // No "Bearer null" for a guest or the server render: no token, no header.
            const sentToken: string = !!token && token !== 'null' && token !== 'undefined' ? token : null;
            if (sentToken) { headers['Authorization'] = `Bearer ${token}`; }
            const toApi = req.url.startsWith(environment.apiUrl);
            const forLocale = toApi && req.url.indexOf('/api/Locale/') >= 0;
            const country = toApi ? this.visitorCountry(forLocale) : null;
            if (country) { headers['X-Visitor-Country'] = country; }
            const ownerCurrency = forLocale ? this.ownerCurrency() : null;
            if (ownerCurrency) { headers['X-Owner-Currency'] = ownerCurrency; }

            const copiedReq = req.clone({ setHeaders: headers });
            if (!toApi) { return next.handle(copiedReq); }
            return next.handle(copiedReq).pipe(
                tap(event => { if (event instanceof HttpResponse) { this.endSession(event.headers, sentToken); } }),
                catchError(err => {
                    if (err instanceof HttpErrorResponse) { this.endSession(err.headers, sentToken); }
                    return throwError(err);
                })); 
        }  
    }
}
